import { _ } from "../../../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import RuleTemplateInfoWidget from "../RuleTemplateInfoWidget/RuleTemplateInfoWidget.js";

/**
 * Make it possible to either view and edit the rule ruleTemplate
 * associated to the current cell or to create a new one.
 *
 * @param {Budget[]} budgets - All budgets the user can pick
 *   for a FromBudgetPredictionRuleModel
 * @param {AbstractRuleTemplateModel} ruleTemplateModel - Widget's model
 *   holding the type of ruleTemplate that is rendered here
 * @param {Renderable} editor - Called with {ruleTemplate, onCancel,
 *   onSave} and should return a renderable of an _editor for
 *   `ruleTemplate`. If no `ruleTemplate` is passed, the function
 *   should create and render a default one.  If `onCancel` is truthy,
 *   a "Cancel" link should appear
 * @param {string} editorDataTestName - data test name of the rule model that is
 *   contained by the rule template
 * @param {function} onCellSelectionRequest - Function called with
 *   {row,column} as argument when the user wants this cell to be
 *   selected
 * @param {AbstractRuleTemplateState} state - State of the RuleTemplatesWidget
 */
export default class RuleTemplateWidget extends Widget {
	constructor({
		budgets,
		ruleTemplateModel,
		editor,
		editorDataTestName,
		onCellSelectionRequest,
		state,
	} = {}) {
		super(...arguments);

		this._budgets = budgets;
		this._ruleTemplateModel = ruleTemplateModel;
		this._editor = editor;
		this._editorDataTestName = editorDataTestName;
		this._onCellSelectionRequest = onCellSelectionRequest;

		this._currentState = state;
	}

	setState(state) {
		this._currentState = state;
		this.update();
	}

	setEditor(editor) {
		this._editor = editor;
		this.update();
	}

	getEditor() {
		return this._editor;
	}

	setRuleTemplateModel(ruleTemplateModel) {
		this._ruleTemplateModel = ruleTemplateModel;
		this.update();
	}

	renderContentOn(html) {
		html.render(
			{
				"data-test": this._editorDataTestName,
			},
			this._ruleTemplateModel.ruleTemplateWidgetContentRenderer(this),
		);
	}

	renderOwnRuleTemplateOn(html) {
		html.render(this._currentState.ownRuleTemplateRenderer(this));
	}

	renderInheritedRuleTemplateOn(html) {
		html.render(this._currentState.inheritedRuleTemplateRenderer(this));
	}

	renderCreateRuleTemplateOn(html) {
		html.render(this._currentState.createRuleTemplateRenderer(this));
	}

	makeRuleTemplateViewer() {
		return new RuleTemplateInfoWidget({
			ruleTemplate: this._ruleTemplateModel.getRuleTemplate(),
			budgets: this._budgets,
		});
	}

	makeInheritedRuleTemplateViewer() {
		const row = this._ruleTemplateModel.getInheritedRow();
		const column = this._ruleTemplateModel.getInheritedColumn();

		return new RuleTemplateInfoWidget({
			ruleTemplate: this._ruleTemplateModel.getRuleTemplate(),
			inheritanceLabel: column
				? this._inheritanceLabelRenderer({ row, column })
				: _("expired period"),
			onInheritanceLinkClick: column && this._onCellSelectionRequest,
		});
	}

	_inheritanceLabelRenderer({ row, column }) {
		return (html) => html.span([row.getTitle(), " ", column.getTitle()]);
	}
}
